import React, { useEffect, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Typography,
  IconButton,
  Switch,
  Divider,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  InvestmentTransactionDTO,
  TransactionCategory,
  transactionExpenseCategories,
  transactionIncomeCategories,
} from '@inversor-hub/types';
import { EurCurrencyField } from '@/components/form/EurCurrencyField';
import { DocumentUploadDropzone } from '@/components/form/DocumentUploadDropzone';
import {
  transactionTypeLabels,
  paymentMethodLabels,
  expenseCategories,
  incomeCategories,
} from './constants';
import { TransactionCategorySelect } from './TransactionCategorySelect';
import { formatCurrency } from '@/utils/misc';

const transactionLineSchema = z.object({
  category: z.string(),
  amount: z.number().int().nullable(),
});

const transactionSchema = z
  .object({
    type: z.enum(['EXPENSE', 'INCOME']),
    category: z.string(),
    concept: z.string().max(500).nullable(),
    amount: z
      .number()
      .int()
      .nullable()
      .refine((val) => val !== null && val > 0, {
        message: 'El importe debe ser mayor que cero',
      }),
    split: z.boolean(),
    lines: z.array(transactionLineSchema).length(2),
    date: z.string().min(1, 'La fecha es obligatoria'),
    paymentMethod: z.enum(['CASH', 'BANK_TRANSFER', 'CARD', 'DIRECT_DEBIT']).optional(),
  })
  .superRefine((data, ctx) => {
    const categoriesForType =
      data.type === 'EXPENSE' ? transactionExpenseCategories : transactionIncomeCategories;

    if (!data.split) {
      if (!data.category) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['category'],
          message: 'La categoría es obligatoria',
        });
        return;
      }

      if (
        !(categoriesForType as readonly TransactionCategory[]).includes(
          data.category as TransactionCategory
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['category'],
          message: 'La categoría no corresponde con el tipo',
        });
      }
      return;
    }

    const lineCategories = new Set<string>();
    let total = 0;

    data.lines.forEach((line, index) => {
      if (!line.category) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'category'],
          message: 'La categoría es obligatoria',
        });
      } else if (
        !(categoriesForType as readonly TransactionCategory[]).includes(
          line.category as TransactionCategory
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'category'],
          message: 'La categoría no corresponde con el tipo',
        });
      }

      if (line.category && lineCategories.has(line.category)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'category'],
          message: 'Elige categorías distintas',
        });
      }
      lineCategories.add(line.category);

      if (line.amount === null || line.amount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'amount'],
          message: 'El importe debe ser mayor que cero',
        });
      } else {
        total += line.amount;
      }
    });

    if (data.amount !== null && total !== data.amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lines'],
        message: 'La suma del desglose debe coincidir con el importe total',
      });
    }
  });

export type TransactionFormData = z.infer<typeof transactionSchema>;

export type TransactionDialogMode = 'create' | 'edit' | 'duplicate';

const dialogTitles: Record<TransactionDialogMode, string> = {
  create: 'Nueva Transacción',
  edit: 'Editar Transacción',
  duplicate: 'Duplicar Transacción',
};

const submitLabels: Record<TransactionDialogMode, string> = {
  create: 'Crear',
  edit: 'Actualizar',
  duplicate: 'Crear copia',
};

interface TransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TransactionFormData, file?: File) => void;
  transaction?: InvestmentTransactionDTO;
  mode?: TransactionDialogMode;
  isSubmitting?: boolean;
}

export const TransactionDialog: React.FC<TransactionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  transaction,
  mode,
  isSubmitting,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const resolvedMode: TransactionDialogMode = mode ?? (transaction ? 'edit' : 'create');
  const isDuplicate = resolvedMode === 'duplicate';
  // Both edit and duplicate start from an existing transaction; only edit saves onto it.
  const prefill = resolvedMode === 'create' ? undefined : transaction;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TransactionFormData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      type: 'EXPENSE',
      category: '',
      concept: '',
      amount: null,
      split: false,
      lines: [
        { category: '', amount: null },
        { category: '', amount: null },
      ],
      date: new Date().toISOString().split('T')[0],
      paymentMethod: undefined,
    },
  });

  const selectedType = watch('type');
  const selectedCategory = watch('category');
  const selectedAmount = watch('amount');
  const isSplit = watch('split');
  const lines = watch('lines');
  const lineErrorMessage = (errors.lines as { message?: string } | undefined)?.message;

  const allocated = (lines?.[0]?.amount ?? 0) + (lines?.[1]?.amount ?? 0);
  const remaining = selectedAmount !== null ? selectedAmount - allocated : null;
  const hasTotal = selectedAmount !== null && selectedAmount > 0;
  const isBalanced = hasTotal && remaining === 0;
  const isOverAllocated = remaining !== null && remaining < 0;
  const allocatedPct = hasTotal
    ? Math.min(100, Math.round((allocated / (selectedAmount as number)) * 100))
    : 0;

  const linePct = (amount: number | null) =>
    hasTotal && amount !== null && amount > 0
      ? Math.round((amount / (selectedAmount as number)) * 100)
      : null;

  // With exactly two lines the split has a single degree of freedom, so editing
  // one amount auto-fills the other with the remainder. This keeps the breakdown
  // always summing to the total instead of forcing the user to do the math.
  const handleLineAmountChange = (index: 0 | 1, value: number | null) => {
    setValue(`lines.${index}.amount`, value, { shouldDirty: true, shouldValidate: true });
    if (selectedAmount !== null && value !== null) {
      const other = index === 0 ? 1 : 0;
      const rest = Math.max(selectedAmount - value, 0);
      setValue(`lines.${other}.amount`, rest, { shouldDirty: true, shouldValidate: true });
    }
  };

  const splitEvenly = () => {
    if (selectedAmount === null) return;
    const half = Math.floor(selectedAmount / 2);
    setValue('lines.0.amount', half, { shouldDirty: true, shouldValidate: true });
    setValue('lines.1.amount', selectedAmount - half, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (prefill) {
      const lines =
        prefill.lines.length > 1
          ? prefill.lines.slice(0, 2)
          : [
              {
                category: prefill.lines[0]?.category ?? prefill.category,
                amount: prefill.lines[0]?.amount ?? prefill.amount,
              },
              { category: '', amount: null },
            ];

      reset({
        type: prefill.type,
        category: prefill.category,
        concept: prefill.concept ?? '',
        amount: prefill.amount,
        split: prefill.lines.length > 1,
        lines: [
          {
            category: lines[0].category,
            amount: lines[0].amount,
          },
          {
            category: lines[1].category,
            amount: lines[1].amount,
          },
        ],
        date: prefill.date.split('T')[0],
        paymentMethod: prefill.paymentMethod,
      });
    } else {
      reset({
        type: 'EXPENSE',
        category: '',
        concept: '',
        amount: null,
        split: false,
        lines: [
          { category: '', amount: null },
          { category: '', amount: null },
        ],
        date: new Date().toISOString().split('T')[0],
        paymentMethod: undefined,
      });
    }
    setUploadedFile(null);
  }, [prefill, reset, open]);

  const handleFormSubmit = (data: TransactionFormData) => {
    onSubmit(data, uploadedFile ?? undefined);
  };

  const categories = selectedType === 'EXPENSE' ? expenseCategories : incomeCategories;

  useEffect(() => {
    if (selectedCategory && !categories.includes(selectedCategory as TransactionCategory)) {
      setValue('category', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [categories, selectedCategory, setValue]);

  const applySplitPreset = (
    lines: [
      { category: TransactionCategory; amount: number | null },
      { category: TransactionCategory; amount: number | null },
    ]
  ) => {
    setValue('split', true, { shouldDirty: true, shouldValidate: true });
    setValue('category', lines[0].category, { shouldDirty: true, shouldValidate: true });
    setValue('lines', lines, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    const categoriesForType = selectedType === 'EXPENSE' ? expenseCategories : incomeCategories;
    const currentLines = watch('lines');
    currentLines.forEach((line, index) => {
      if (line.category && !categoriesForType.includes(line.category as TransactionCategory)) {
        setValue(`lines.${index}.category`, '', { shouldDirty: true, shouldValidate: true });
      }
    });
  }, [selectedType, setValue, watch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{dialogTitles[resolvedMode]}</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {isDuplicate && (
              <Alert severity="info" icon={<ContentCopyIcon fontSize="inherit" />}>
                Se creará una transacción nueva con estos datos. Revisa la fecha y el importe antes
                de guardar.
                {transaction?.document && ' El documento adjunto no se copia.'}
              </Alert>
            )}

            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Tipo</InputLabel>
                  <Select {...field} label="Tipo">
                    {Object.entries(transactionTypeLabels).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="amount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <EurCurrencyField
                  value={value}
                  onChange={onChange}
                  label={isSplit ? 'Importe total' : 'Importe'}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                />
              )}
            />

            <Box>
              <Controller
                name="split"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(event) => {
                          field.onChange(event.target.checked);
                          if (event.target.checked) {
                            setValue('lines', [
                              {
                                category: selectedCategory,
                                amount: null,
                              },
                              { category: '', amount: null },
                            ]);
                          } else {
                            setValue('lines', [
                              { category: '', amount: null },
                              { category: '', amount: null },
                            ]);
                          }
                        }}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        Desglosar en categorías
                        <Tooltip
                          title="Un mismo pago puede pertenecer a dos categorías a efectos fiscales. Desglosarlo te ayuda a deducir correctamente cada parte."
                          arrow
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ color: 'text.disabled', cursor: 'help' }}
                          />
                        </Tooltip>
                      </Box>
                    }
                  />
                )}
              />
              {!isSplit && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25, ml: 0.5 }}
                >
                  Actívalo si este pago incluye dos conceptos distintos. Por ejemplo, la cuota de la
                  hipoteca se reparte entre intereses (deducibles) y devolución de capital.
                </Typography>
              )}
            </Box>

            {!isSplit && (
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <TransactionCategorySelect
                    value={field.value as TransactionCategory | ''}
                    onChange={field.onChange}
                    type={selectedType}
                    error={!!errors.category}
                    helperText={errors.category?.message}
                  />
                )}
              />
            )}

            {isSplit && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: isOverAllocated
                    ? 'error.main'
                    : isBalanced
                      ? 'success.main'
                      : 'divider',
                  borderRadius: 1,
                  p: 2,
                  transition: 'border-color 150ms ease',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={1}
                >
                  <Typography variant="subtitle2">Desglose</Typography>
                  {isBalanced ? (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleRoundedIcon />}
                      label="Cuadrado"
                    />
                  ) : isOverAllocated ? (
                    <Chip
                      size="small"
                      color="error"
                      label={`Te has pasado ${formatCurrency(Math.abs(remaining as number))}`}
                    />
                  ) : (
                    <Chip
                      size="small"
                      color={hasTotal ? 'warning' : 'default'}
                      variant={hasTotal ? 'filled' : 'outlined'}
                      label={
                        hasTotal
                          ? `Restante ${formatCurrency(remaining as number)}`
                          : 'Indica el importe total'
                      }
                    />
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Elige las dos categorías e indica el importe de una. Calcularemos la otra
                  automáticamente para que el total siempre cuadre.
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={allocatedPct}
                  color={isOverAllocated ? 'error' : isBalanced ? 'success' : 'primary'}
                  sx={{ height: 6, borderRadius: 3, mb: 0.75 }}
                />
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  {hasTotal
                    ? `Asignado ${formatCurrency(allocated)} de ${formatCurrency(selectedAmount as number)}`
                    : 'Reparte el importe entre dos categorías.'}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  mb={2}
                  flexWrap="wrap"
                  alignItems="center"
                  useFlexGap
                >
                  <Typography variant="caption" color="text.secondary">
                    Atajos:
                  </Typography>
                  {selectedType === 'EXPENSE' && (
                    <>
                      <Tooltip
                        title="Rellena las categorías de una cuota de hipoteca: intereses (deducibles) y devolución de capital."
                        arrow
                      >
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            applySplitPreset([
                              { category: 'FINANCING_INTEREST', amount: null },
                              { category: 'LOAN_PRINCIPAL', amount: null },
                            ])
                          }
                        >
                          Hipoteca
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title="Rellena las categorías de una obra: conservación y reparación, y mejora."
                        arrow
                      >
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            applySplitPreset([
                              { category: 'REPAIRS_MAINTENANCE', amount: null },
                              { category: 'IMPROVEMENT', amount: null },
                            ])
                          }
                        >
                          Reforma
                        </Button>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip
                    title={
                      hasTotal
                        ? 'Reparte el importe total a partes iguales entre las dos categorías.'
                        : 'Indica antes el importe total.'
                    }
                    arrow
                  >
                    <span>
                      <Button
                        type="button"
                        size="small"
                        variant="text"
                        disabled={!hasTotal}
                        onClick={splitEvenly}
                      >
                        Dividir 50/50
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>

                {[0, 1].map((index) => {
                  const pct = linePct(lines?.[index]?.amount ?? null);
                  const amountError = errors.lines?.[index]?.amount?.message;
                  return (
                    <Box key={index}>
                      {index > 0 && <Divider sx={{ my: 2 }} />}
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', sm: '1fr 180px' }}
                        gap={2}
                      >
                        <Controller
                          name={`lines.${index}.category`}
                          control={control}
                          render={({ field }) => (
                            <TransactionCategorySelect
                              value={field.value as TransactionCategory | ''}
                              onChange={(category) => {
                                field.onChange(category);
                                if (index === 0) {
                                  setValue('category', category, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                }
                              }}
                              type={selectedType}
                              label={`Categoría ${index + 1}`}
                              error={!!errors.lines?.[index]?.category}
                              helperText={errors.lines?.[index]?.category?.message}
                            />
                          )}
                        />

                        <Controller
                          name={`lines.${index}.amount`}
                          control={control}
                          render={({ field: { value } }) => (
                            <EurCurrencyField
                              value={value}
                              onChange={(next) => handleLineAmountChange(index as 0 | 1, next)}
                              label={`Importe ${index + 1}`}
                              error={!!amountError}
                              helperText={
                                amountError ?? (pct !== null ? `${pct}% del total` : undefined)
                              }
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  );
                })}

                {lineErrorMessage && !isOverAllocated && (
                  <Typography variant="caption" color="error" display="block" mt={1}>
                    {lineErrorMessage}
                  </Typography>
                )}
              </Box>
            )}

            <Controller
              name="concept"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Concepto / Notas (Opcional)"
                  multiline
                  rows={2}
                  fullWidth
                  error={!!errors.concept}
                  helperText={errors.concept?.message}
                />
              )}
            />

            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label="Fecha"
                  fullWidth
                  error={!!errors.date}
                  helperText={
                    errors.date?.message ??
                    (isDuplicate ? 'Comprueba que la fecha de la copia es correcta' : undefined)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="paymentMethod"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Método de Pago (Opcional)</InputLabel>
                  <Select {...field} label="Método de Pago (Opcional)">
                    <MenuItem value="">
                      <em>Ninguno</em>
                    </MenuItem>
                    {Object.entries(paymentMethodLabels).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <DocumentUploadDropzone file={uploadedFile} onFileChange={setUploadedFile} />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {submitLabels[resolvedMode]}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
